import { useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import HelpPopover from '../help/HelpPopover'
import ConfidenceGate from '../shared/ConfidenceGate'

/**
 * The Assessment card (figure S3) — the three axis scores for the WHOLE
 * invention, and the sentences that make them mean something.
 *
 * Two rules shape it. First, the scores are the ones the assessment engine
 * stored, on the axes it actually computes; nothing is renamed and nothing is
 * synthesised. Second, every explanatory block below the tiles is rendered only
 * when its source exists — the analysis's own novel aspects, its own overlap
 * areas, its own reasoning. Where the analysis said nothing, this card says
 * nothing rather than filling the space with a plausible sentence.
 */
export interface AssessmentAxis {
  key: string
  /** The axis name as the engine computes it. */
  label: string
  /** Rendered under the label in the figure's smaller type, e.g. "whole invention". */
  qualifier?: string
  score: number
  /** The band the engine recorded (Strong / Good / Moderate / Fair …). */
  band?: string
  tone: 'ok' | 'warn' | 'info'
}

export interface AssessmentPanelProps {
  axes?: AssessmentAxis[]
  /** How many documents the assessment was computed across, and how many were set aside. */
  documentsCounted?: number
  documentsSetAside?: number
  computedAt?: string
  reviewApproved?: boolean
  /** The analysis's own reasoning for the scores. Empty when it recorded none. */
  reasoning?: string[]
  /** ai_analysis.novel_aspects — what is novel only when read together. */
  novelTogether?: string[]
  /** ai_analysis.overlap_areas — what the corpus already carries. */
  alreadyInTheArt?: string[]
  /** A document scoring far above the invention as a whole, if one does. */
  dissenting?: { name: string; score: number; inventionScore: number } | null
  /**
   * Set when the engine ran and recorded `recommendation: 'insufficient_basis'`
   * (Story #6172 F11) — there wasn't enough real content to score honestly, so
   * it stored an honest null result instead of a fabricated number. When
   * present, this replaces the tile grid with ConfidenceGate; the "no axes at
   * all" empty state below (assessment never ran) is unaffected.
   */
  insufficientBasisReason?: string
  onViewFull?: () => void
  onReviewWhatCounts?: () => void
}

const TONE = {
  ok: { text: 'text-emerald-600', bar: 'bg-emerald-500', border: 'border-emerald-200' },
  warn: { text: 'text-amber-600', bar: 'bg-amber-500', border: 'border-amber-200' },
  info: { text: 'text-iflow-600', bar: 'bg-iflow-500', border: 'border-iflow-200' },
}

/**
 * UX-021: a number without a definition invites the wrong conclusion, so each
 * axis states in one line what it measures. Keyed by the engine's own axis key;
 * an axis the engine adds later simply shows no definition rather than a guess.
 */
export const AXIS_DEFINITIONS: Record<string, string> = {
  novelty: 'how different your claims look from existing documents',
  feasibility: 'how buildable the method looks from what is described',
  market_potential: 'how much room there is for it in the market',
}

export const NO_EXPLANATION_SAVED = 'No written explanation was saved for these scores.'

const HOW_SCORED =
  'Novelty reflects how distinct the claims look against the loaded corpus; feasibility weighs how buildable the method is; market potential how much room there is for it. A starting point for your judgement, not a legal opinion.'

export default function AssessmentPanel({
  axes,
  documentsCounted,
  documentsSetAside = 0,
  computedAt,
  reviewApproved,
  reasoning,
  novelTogether,
  alreadyInTheArt,
  dissenting,
  insufficientBasisReason,
  onViewFull,
  onReviewWhatCounts,
}: AssessmentPanelProps) {
  const explained = (reasoning ?? []).filter((r) => r && r.trim())
  const [howScoredOpen, setHowScoredOpen] = useState(false)

  return (
    <section data-part="AssessmentPanel" className="light-card p-4">
      <div className="mb-2 flex items-center justify-between gap-3">
        <h3 className="flex items-center gap-1.5 text-[13px] font-bold text-iflow-900">
          📊 Assessment
          <HelpPopover
            topic="concept-assessment-axes"
            title="What the scores mean"
            body={HOW_SCORED}
            size={14}
          />
        </h3>
        {axes && axes.length > 0 && onViewFull && (
          <button type="button" onClick={onViewFull} className="text-[12px] font-semibold text-iflow-600 hover:text-iflow-700">
            View full assessment →
          </button>
        )}
      </div>

      {insufficientBasisReason ? (
        <ConfidenceGate
          icon={<AlertTriangle />}
          title="Not enough to assess yet"
          description={insufficientBasisReason}
          compact
        />
      ) : !axes || axes.length === 0 ? (
        <p className="text-[12.5px] text-gray-500">
          The three scores appear here once the assessment has run. Upload a document and run the
          analysis to produce them.
        </p>
      ) : (
        <>
          <p className="mb-3 text-[11.5px] text-gray-500">
            Computed across <b className="text-gray-700">{documentsCounted ?? 0} document{documentsCounted === 1 ? '' : 's'} that make up this invention</b>
            {' '}and the intake{computedAt ? `, ${computedAt}` : ''}.
            {documentsSetAside > 0 && (
              <>
                {' '}<b className="text-gray-700">{documentsSetAside} more set aside</b> as reference or supporting
                {onReviewWhatCounts && (
                  <>
                    {' — '}
                    <button type="button" onClick={onReviewWhatCounts} className="font-semibold text-iflow-600 hover:text-iflow-700">
                      review what counts
                    </button>
                  </>
                )}
                .
              </>
            )}
            {!reviewApproved && ' Approve the review to unlock matching.'}
          </p>

          <div className="grid gap-3 sm:grid-cols-3">
            {axes.map((axis) => (
              <div key={axis.key} className={`rounded-xl border bg-white p-3 ${TONE[axis.tone].border}`}>
                <div className="text-[12px] font-semibold uppercase tracking-wide text-gray-600">
                  {axis.label}
                  {axis.qualifier ? <span className="font-normal opacity-70"> · {axis.qualifier}</span> : null}
                </div>
                {AXIS_DEFINITIONS[axis.key] && (
                  <p data-testid={`axis-definition-${axis.key}`} className="mt-0.5 text-[12px] leading-snug text-gray-600">
                    {axis.label}: {AXIS_DEFINITIONS[axis.key]}
                  </p>
                )}
                <div className={`mt-1.5 text-[28px] font-extrabold leading-none ${TONE[axis.tone].text}`}>
                  {axis.score}
                  <span className="text-[13px] font-bold text-gray-500">/100</span>
                </div>
                {axis.band && <div className={`mb-1.5 text-[11.5px] font-bold ${TONE[axis.tone].text}`}>{axis.band}</div>}
                <div className="h-1.5 overflow-hidden rounded-full bg-slate-200">
                  <div className={`h-full rounded-full ${TONE[axis.tone].bar}`} style={{ width: `${Math.max(0, Math.min(100, axis.score))}%` }} />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-2">
            <button
              type="button"
              aria-expanded={howScoredOpen}
              onClick={() => setHowScoredOpen((o) => !o)}
              className="text-[12px] font-semibold text-iflow-600 hover:text-iflow-700"
            >
              How are these scored?
            </button>
            {howScoredOpen && (
              <p data-testid="how-scored" className="mt-1 text-[12px] leading-relaxed text-gray-600">{HOW_SCORED}</p>
            )}
          </div>

          {explained.length > 0 ? (
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-iflow-200 bg-iflow-50/70 px-3 py-2 text-[12px] leading-relaxed text-gray-700">
              <span aria-hidden>💡</span>
              <p>
                <b className="text-iflow-900">What this means.</b> {explained.join(' ')}
              </p>
            </div>
          ) : (
            <p className="mt-2 text-[12px] text-gray-600">{NO_EXPLANATION_SAVED}</p>
          )}

          {(novelTogether?.length || alreadyInTheArt?.length) ? (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {novelTogether && novelTogether.length > 0 && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3">
                  <h4 className="mb-1 text-[12px] font-bold text-emerald-800">✦ Novel together</h4>
                  <p className="text-[11.5px] leading-relaxed text-gray-700">{novelTogether.join(' · ')}</p>
                </div>
              )}
              {alreadyInTheArt && alreadyInTheArt.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3">
                  <h4 className="mb-1 text-[12px] font-bold text-amber-800">◑ Already in the art</h4>
                  <p className="text-[11.5px] leading-relaxed text-gray-700">{alreadyInTheArt.join(' · ')}</p>
                </div>
              )}
            </div>
          ) : null}

          {dissenting && (
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50/70 px-3 py-2 text-[12px] leading-relaxed text-gray-700">
              <span aria-hidden>⚠</span>
              <p>
                <b className="text-amber-800">One document disagrees with the whole.</b>{' '}
                <b>{dissenting.name}</b> scores <b>{dissenting.score} on its own</b>, against{' '}
                {dissenting.inventionScore} for the invention read as one. The invention-level view
                governs here — worth reading before you rely on that document alone.
              </p>
            </div>
          )}
        </>
      )}
    </section>
  )
}
